// Client for the traceira-backend onboarding API (v0, experimental). This is the
// only module allowed to make network requests. It calls this site's own /api,
// which the Vite dev server or the cluster's nginx forwards to the backend.
/* eslint-disable no-restricted-globals */

const API_URL = '';
import { useSession } from '../../state/session.ts';
import { redirectToExpiredSignIn } from '../auth/sessionExpiry.ts';
import type {
  ActorProfile,
  WorkspaceListItem,
  WorkspaceMandateSummary,
  WorkspaceSession,
} from '../../state/session.ts';
import type {
  AuthorizationInput,
  Letter,
  AuthorizationConfig,
  DomainLetterResult,
} from '../authorization/model.ts';
import type { EngagementView, MemoryEntry, RunEventsPage, RunView } from '../engagement/types.ts';
import type { AuditActivity, AuditLogPage } from '../audit/model.ts';
import type { FindingReview, ReviewDecision } from '../findings/types.ts';
import type {
  RemediationInput,
  RemediationRecord,
  RetestInput,
  RetestRecord,
} from '../remediation/types.ts';
import type { ReportFormat, ReportRevision } from '../records/reportsStore.ts';
import { withoutToolNames } from '../discovery/toolNames.ts';
import type {
  CreateImportJobInput,
  CreateReportInput,
  EvidenceRecord,
  EvidenceReport,
  Finding,
  FindingFilters,
  ImportJob,
  RecordsScope,
  SourceRecord,
  WorkflowRun,
} from '../records/types.ts';
import type {
  TraceiraWorkspaceDiscoveryCapabilityEligibilityV1Provisional,
  TraceiraWorkspaceDiscoveryRecordsV1,
  StartDiscoveryRequest,
  StartDiscoveryResponse,
  StopDiscoveryResponse,
  EligibilityReason,
  ReasonCode,
  Finding as WorkspaceFinding,
  Evidence as WorkspaceEvidence,
  SourceRecord as WorkspaceSourceRecord,
} from '../../contracts/workspace/v1/index.ts';

export type DiscoveryStopResponse = StopDiscoveryResponse;

export type DiscoveryHeartbeatState = 'NOT_STARTED' | 'HEALTHY' | 'STALE' | 'FINISHED';

export interface DiscoveryProgressSummary {
  totalUnits: number;
  completedUnits: number;
  failedUnits: number;
  partialUnits: number;
  skippedUnits: number;
  runningUnits: number;
  notAttemptedUnits: number;
}

export interface DiscoveryProgressUnit {
  coverageUnitId: string;
  targetType: string;
  targetValue: string;
  method: 'DNS_QUERY' | 'NETWORK_PROBE';
  status: 'NOT_ATTEMPTED' | 'RUNNING' | 'PARTIAL' | 'COMPLETE' | 'FAILED' | 'SKIPPED';
  startedAt?: string;
  finishedAt?: string;
  limitation?: string;
}

export interface DiscoveryProgressResponse {
  attemptId: string;
  status: 'QUEUED' | 'RUNNING' | 'STOP_REQUESTED' | 'STOPPED' | 'COMPLETE' | 'PARTIAL' | 'FAILED';
  profile: {
    profileId: string;
    profileVersion: number;
  };
  summary: DiscoveryProgressSummary;
  units: DiscoveryProgressUnit[];
  blockers: string[];
  lastUsefulProgress?: string;
  heartbeatState: DiscoveryHeartbeatState;
  cleanup?: {
    state: 'PENDING' | 'CONFIRMED' | 'FAILED' | 'NOT_REQUIRED';
    confirmedAt?: string;
    receiptId?: string;
    details?: string;
  };
  startedAt?: string;
  finishedAt?: string;
  findings: Array<{
    findingId: string;
    title: string;
    lane: 'DNS' | 'Network' | 'Mixed';
    normalizedSeverity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFORMATIONAL' | 'UNKNOWN';
  }>;
}

export type {
  TraceiraWorkspaceDiscoveryCapabilityEligibilityV1Provisional,
  EligibilityReason,
  ReasonCode,
  ActorProfile,
  WorkspaceListItem,
  WorkspaceMandateSummary,
};

export type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';
export type OrgRole = 'owner' | 'admin' | 'member';

export interface WorkspaceMember {
  actorId: string;
  sub?: string;
  email: string | null;
  displayName?: string | null;
  role: WorkspaceRole;
  /** 'organization': an org owner/admin's implicit admin access, not removable here. */
  via?: 'workspace' | 'organization';
  status?: 'active' | 'invited' | 'disabled';
  createdAt: string;
}

export interface WorkspaceInvite {
  email: string;
  role?: Exclude<WorkspaceRole, 'owner'>;
  createdAt: string;
}

export interface OrgListItem {
  id: string;
  name: string;
  role: OrgRole;
  permissions: string[];
  createdAt: string;
}

export interface OrgDetail extends OrgListItem {
  workspaces: {
    id: string;
    name: string;
    domain: string;
    verifiedAt: string;
    role: WorkspaceRole;
    via?: 'workspace' | 'organization';
  }[];
}

export interface OrgMember {
  actorId: string;
  email: string | null;
  displayName: string | null;
  role: OrgRole;
  createdAt: string;
}

export interface OrgInvite {
  email: string;
  role: Exclude<OrgRole, 'owner'>;
  createdAt: string;
}

export interface WorkspaceMembersResponse {
  members: WorkspaceMember[];
  invites: WorkspaceInvite[];
}

export type ContextSourceStatus = 'connecting' | 'active' | 'degraded' | 'disconnected';

/** A connected source (Settings → Sources), as the backend lists it. */
export interface ContextSource {
  id: string;
  kind: string;
  status: ContextSourceStatus;
  custody: 'store' | 'live';
  memoryScope: 'workspace' | 'org';
  label?: string;
  lastSyncAt: string | null;
  lastError: string | null;
  /** GitLab and Bitbucket: the repositories to pick from, the picked IDs and the client's webhook. */
  config?: {
    availableRepos?: { id: string; fullName: string }[];
    repositories?: string[];
    webhook?: { url: string; secretFingerprints: string[] };
  };
  /** A client's MCP connector: its origin, its own name, the tools it offers, what Traceira reads. */
  mcp?: {
    url: string;
    /** The client's name for it (also the source's label), and what it is; `''` without one. */
    name: string;
    description: string;
    tools: McpTool[];
    /** The header that carries the API key, or null without a key; never the key. */
    authHeader: string | null;
    resources: McpResources;
  };
}

/** An MCP server's resources, in its server's words: a sample and its URI templates. */
export interface McpResources {
  /** The server offers resources at all. */
  supported: boolean;
  /** Traceira may read them. */
  read: boolean;
  /** Traceira keeps a searchable copy (store custody only). */
  index: boolean;
  templates: {
    uriTemplate: string;
    name: string;
    title?: string;
    description?: string;
    mimeType?: string;
  }[];
  sample: { uri: string; name: string; title?: string; mimeType?: string }[];
}

/** One tool of a client's MCP server, in its server's words. */
export interface McpTool {
  name: string;
  title: string | null;
  description: string | null;
  /** `default`: the tool says it only reads. */
  offer: 'default' | 'more';
  allowed: boolean;
  /** Approved, but its definition changed since. */
  changed: boolean;
}

export interface ContextConnectInput {
  kind: string;
  custody: ContextSource['custody'];
  memoryScope: ContextSource['memoryScope'];
}

export interface ContextOrgSettings {
  engagementMemoryScope: 'workspace' | 'org';
}

export interface ServerWorkspaceDetail {
  id: string;
  name: string;
  domain: string;
  verifiedAt: string;
  verifiedThrough: string;
  createdAt: string;
  role?: string;
  membershipStatus?: string;
  organization?: { id: string; name: string } | null;
  permissions?: string[];
  via?: 'workspace' | 'organization';
  mandate: WorkspaceMandateSummary;
  container?: ContainerView;
}

export interface OwnerClaimResult {
  ok: boolean;
  membership: {
    id: string;
    workspaceId: string;
    actorId: string;
    role: string;
    status: string;
    sourceLetterId: string | null;
    createdAt: string;
  };
  transferred?: boolean;
}

export interface ServerLine {
  time: string;
  glyph: string;
  text: string;
  duration?: string;
  group?: number;
}

export interface ServerProvider {
  kind: 'cloudflare' | 'other' | 'unknown';
  name: string;
  shortName: string;
  path: string[] | null;
  hasProxy: boolean;
}

export interface ServerCheck {
  outcome: 'complete' | 'missing' | 'timeout' | 'unreachable' | 'unsafe' | 'stopped';
  zone: string | null;
  nameservers: string[];
  provider: ServerProvider | null;
  authoritative: { host: string; ip: string } | null;
  nameConflict: { type: 'CNAME' | 'TXT'; value: string } | null;
  lookups: number;
}

export interface ServerSession {
  id: string;
  organization: string;
  authorizer?: { name: string; email: string };
  domain: string;
  methods: { CNAME: boolean; TXT: boolean };
  record: { type: 'CNAME' | 'TXT'; name: string; fullName: string; value: string };
  check: ServerCheck | null;
  recordState: {
    status: 'not-checked' | 'waiting' | 'verified' | 'proxy' | 'wrong' | 'conflict' | 'error';
    found: string | null;
    resolvers: {
      name: string;
      server: string;
      state: 'found' | 'different' | 'not-yet' | 'unknown';
    }[];
    attempts: number;
    lastCheckedAt: string | null;
  };
  verifiedAt: string | null;
  verifiedThrough: string | null;
  consent: { actor: string; acceptedAt: string; statementVersion: string } | null;
  domainChanged: boolean;
  workspaceId: string | null;
  canCreateWorkspace: boolean;
}

export type ContainerPhase = 'none' | 'creating' | 'running' | 'failed' | 'removed' | 'unavailable';
export type StageName =
  'requested' | 'scheduled' | 'storage_ready' | 'image_ready' | 'started' | 'running';
export type StageStatus = 'done' | 'active' | 'pending' | 'failed';

export interface ContainerStage {
  name: StageName;
  label: string;
  status: StageStatus;
  at: string | null;
}

export interface ContainerNotice {
  code: string;
  title: string;
  message: string;
  at: string | null;
}

/** Live-only terminal capability (terminal broker contract §1); stored views must not drive it. */
export interface TerminalCapability {
  available: boolean;
  state: 'none' | 'connecting' | 'connected' | 'stopping';
  reason: string | null;
}

export interface TerminalTicket {
  ticket: string;
  webSocketPath: string;
  protocol: string;
  sessionId: string;
  expiresAt: string;
}

export interface ContainerView {
  phase: ContainerPhase;
  stages: ContainerStage[];
  attempt: number | null;
  createdAt: string | null;
  readyDeadline: string | null;
  expiresAt: string | null;
  observedAt: string | null;
  stale: boolean;
  notice: ContainerNotice | null;
  canStart: boolean;
  pollAfterMs: number | null;
  /** Optional: stored or older views lack it. */
  terminal?: TerminalCapability;
}

export interface ServerWorkspace {
  authorizer?: { name: string; email: string };
  id: string;
  name: string;
  domain: string;
  verifiedAt: string;
  verifiedThrough: string;
  container?: ContainerView;
}

/** One-click record setup through the DNS provider (Domain Connect). */
export interface ServerDomainConnect {
  status: 'not-ready' | 'unsupported' | 'not-configured' | 'available';
  provider: { id: string; name: string; controlPanelUrl: string | null } | null;
  method: 'CNAME' | 'TXT';
  applyUrl: string | null;
  /** The provider has onboarded Traceira's template (its page may refuse until then). */
  templateApproved: boolean;
}

export class ApiError extends Error {
  /** 429 Retry-After in whole seconds, read from the response header (body value as fallback). */
  readonly retryAfterSeconds: number | null;
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: Record<string, unknown>,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** The backend sends Retry-After as a response header; the body value is a fallback. */
function retryAfterSecondsOf(res: Response, body: Record<string, unknown>): number | null {
  const header = Number.parseInt(res.headers.get('retry-after') ?? '', 10);
  if (Number.isFinite(header) && header > 0) return header;
  const fromBody = Number(body.retryAfter);
  return Number.isFinite(fromBody) && fromBody > 0 ? fromBody : null;
}

let signInRedirected = false;
let renewal: Promise<SessionRenewal> | null = null;

/** 'ended' means sign in again; 'unavailable' is a network or provider problem, not a sign-out. */
export type SessionRenewal = 'renewed' | 'ended' | 'unavailable';

/**
 * Renews the short session cookie from the refresh cookie. One renewal runs at a time in this
 * tab, and a lock serialises tabs because the refresh token rotates on use.
 */
function renewSession(): Promise<SessionRenewal> {
  renewal ??= (async () => {
    const run = async (): Promise<SessionRenewal> => {
      try {
        const res = await fetch(`${API_URL}/api/v0/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) return 'renewed';
        return res.status >= 500 ? 'unavailable' : 'ended';
      } catch {
        return 'unavailable';
      }
    };
    return navigator.locks ? navigator.locks.request('traceira-session-refresh', run) : run();
  })().finally(() => {
    renewal = null;
  });
  return renewal;
}

function isWorkspaceMemberRoute(path: string): boolean {
  return /^\/api\/v1\/workspaces\/[^/]+\/(?:discovery(?:\/|$)|terminal\/questions(?:\?|$))/.test(
    path,
  );
}

/** Calls that carry their own credential (setup session, owner token) report their own 401s. */
function usesSessionCookie(path: string, init: RequestInit): boolean {
  if (path.startsWith('/api/v0/auth/')) return false;
  const headers = new Headers(init.headers);
  // These workspace routes authenticate from the session cookie; their setup/owner capability
  // headers are ignored by authorizeWorkspaceAccess and must not suppress session renewal.
  if (isWorkspaceMemberRoute(path)) {
    const bearer = headers
      .get('authorization')
      ?.match(/^Bearer\s+(.+)$/i)?.[1]
      ?.trim();
    return !bearer || bearer.split('.').length !== 3;
  }
  return !headers.has('authorization') && !headers.has('x-traceira-session');
}

/** /me is answered by the router, which does its own redirect; public pages have no session. */
function endedSession(path: string): boolean {
  if (path.startsWith('/api/v0/me')) return false;
  const state = useSession.getState();
  return (
    (Boolean(state.actor) || (isWorkspaceMemberRoute(path) && Boolean(state.workspace))) &&
    !window.location.pathname.startsWith('/sign-in')
  );
}

/** The body as JSON ({} when empty or invalid), with tool names hidden in every string. */
const readJson = (res: Response): Promise<unknown> =>
  res
    .text()
    .then((text) => JSON.parse(text, withoutToolNames) as unknown)
    .catch(() => ({}));

/** Sends a request with the session's renewal and error handling; the caller reads the body. */
async function send(path: string, init: RequestInit = {}, renewed = false): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...init.headers },
    });
  } catch {
    throw new ApiError(
      0,
      'offline',
      "We can't reach Traceira right now. Your input is saved; try again in a moment.",
      {},
    );
  }
  if (res.ok) return res;
  const body = (await readJson(res)) as Record<string, unknown>;
  const sessionCookieRoute = usesSessionCookie(path, init);
  const workspaceMemberRoute = isWorkspaceMemberRoute(path);
  if (
    res.status === 401 &&
    sessionCookieRoute &&
    (useSession.getState().actor || workspaceMemberRoute)
  ) {
    // The cookie lapsed: renew it from the refresh cookie and repeat the call once. A visitor
    // who was never signed in has nothing to renew, so they skip the extra request.
    const outcome = renewed ? 'ended' : await renewSession();
    if (outcome === 'renewed') return send(path, init, true);
    // Not a 401: the router reads 401 as signed out, and this session may still renew.
    if (outcome === 'unavailable')
      throw new ApiError(
        503,
        'unavailable',
        'Sign-in is temporarily unavailable. Try again shortly.',
        {},
      );
    if (!signInRedirected && endedSession(path)) {
      signInRedirected = true;
      redirectToExpiredSignIn(`${window.location.pathname}${window.location.search}`);
    }
  }
  throw new ApiError(
    res.status,
    String(body.error ?? 'error'),
    String(body.message ?? res.statusText),
    body,
    retryAfterSecondsOf(res, body),
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await send(path, init);
  return (await readJson(res)) as T;
}

const json = (value: unknown) => JSON.stringify(value);

// Either credential is enough; a header is sent only when its value is non-empty.
const credentials = (session: string, token: string) => ({
  ...(session ? { 'X-Traceira-Session': session } : {}),
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

/** The callbacks a streamed terminal answer invokes as each server-sent event arrives. */
export interface AnswerStreamHandlers {
  onThinking: (delta: string) => void;
  onAnswer: (answer: {
    answer: string;
    rationale?: string;
    verified: boolean | null;
    answeredAt?: string;
  }) => void;
  onError: (code: string) => void;
}

/** Reads a `text/event-stream` body and dispatches thinking / answer / error events. */
async function consumeAnswerStream(res: Response, handlers: AnswerStreamHandlers): Promise<void> {
  const body = res.body;
  if (!body) {
    handlers.onError('assistant_failed');
    return;
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = 'message';
  let sawAnswer = false;
  let sawError = false;

  const dispatch = (data: string) => {
    if (eventName === 'thinking') {
      try {
        const parsed = JSON.parse(data) as { delta?: unknown };
        if (typeof parsed.delta === 'string' && parsed.delta) handlers.onThinking(parsed.delta);
      } catch {
        // Ignore a malformed thinking delta.
      }
    } else if (eventName === 'answer') {
      try {
        const parsed = JSON.parse(data) as {
          answer?: unknown;
          rationale?: unknown;
          verified?: unknown;
          answeredAt?: unknown;
        };
        if (typeof parsed.answer === 'string' && parsed.answer.trim()) {
          sawAnswer = true;
          handlers.onAnswer({
            answer: parsed.answer,
            ...(typeof parsed.rationale === 'string' && parsed.rationale.trim()
              ? { rationale: parsed.rationale.trim() }
              : {}),
            verified: typeof parsed.verified === 'boolean' ? parsed.verified : null,
            ...(typeof parsed.answeredAt === 'string' ? { answeredAt: parsed.answeredAt } : {}),
          });
        }
      } catch {
        // Ignore a malformed answer event.
      }
    } else if (eventName === 'error') {
      sawError = true;
      try {
        const parsed = JSON.parse(data) as { error?: unknown };
        handlers.onError(typeof parsed.error === 'string' ? parsed.error : 'assistant_failed');
      } catch {
        handlers.onError('assistant_failed');
      }
    }
    // 'done' and 'message' are ignored.
  };

  const processLine = (line: string) => {
    const trimmed = line.endsWith('\r') ? line.slice(0, -1) : line;
    if (trimmed.startsWith('event:')) {
      eventName = trimmed.slice('event:'.length).trim();
      return;
    }
    if (trimmed.startsWith('data:')) {
      const data = trimmed.slice('data:'.length).trim();
      if (data) dispatch(data);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) processLine(line);
    }
    buffer += decoder.decode();
    for (const line of buffer.split('\n')) processLine(line);
  } catch {
    // Stream interrupted; fall through to the missing-event guard below.
  }

  if (!sawAnswer && !sawError) handlers.onError('assistant_failed');
}

const terminalSessions = (authorizationId: string) =>
  `/api/v0/authorization/${encodeURIComponent(authorizationId)}/environment/terminal-sessions`;

const evidenceV1ScopeHeaders = (scope: RecordsScope) => ({
  'X-Traceira-Tenant-Id': scope.tenantId,
  'X-Traceira-Workspace-Id': scope.workspaceId,
  'X-Traceira-Engagement-Id': scope.engagementId,
});

const evidenceV1Path = (resource: string, id?: string) =>
  `/api/v1/${resource}${id ? `/${encodeURIComponent(id)}` : ''}`;

const contextPath = (workspaceId: string, rest: string) =>
  `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/context${rest}`;

export const api = {
  // Customer Identity and Sessions (FLOW-01C, FLOW-01E)
  me: (token?: string) =>
    request<ActorProfile>('/api/v0/me', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }),
  /** External sign-in providers (Google, Microsoft, GitHub…) the backend's broker federates. */
  signInProviders: () =>
    request<{ available: boolean; providers: { id: string; name: string }[] }>(
      '/api/v0/auth/providers',
    ),
  updateMe: (input: { displayName: string }) =>
    request<ActorProfile>('/api/v0/me', { method: 'PATCH', body: json(input) }),
  myWorkspaces: (token?: string) =>
    request<{ workspaces: WorkspaceListItem[] }>('/api/v0/me/workspaces', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    }),
  signOut: async (token?: string) => {
    const res = await request<{ ok: boolean; signedOut: boolean; logoutUrl?: string | null }>(
      '/api/v0/auth/sign-out',
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      },
    );
    if (typeof window !== 'undefined' && window.location) {
      if (res.logoutUrl) {
        window.location.assign(res.logoutUrl);
      } else {
        window.location.assign('/sign-in');
      }
    }
    return res;
  },
  ownerClaim: (workspaceId: string, letterId: string, proofToken: string, token?: string) =>
    request<OwnerClaimResult>(`/api/v0/workspaces/${encodeURIComponent(workspaceId)}/owner-claim`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: json({ letterId, ownerToken: proofToken }),
      credentials: 'include',
    }),
  workspace: (id: string, token?: string, session?: string) =>
    request<ServerWorkspaceDetail>(`/api/v0/workspaces/${encodeURIComponent(id)}`, {
      headers: {
        ...(session ? { 'X-Traceira-Session': session } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    }),
  updateWorkspace: (id: string, input: { name: string }) =>
    request<{ id: string; name: string }>(`/api/v0/workspaces/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: json(input),
    }),
  workspaceMembers: (workspaceId: string, token?: string) =>
    request<WorkspaceMembersResponse>(
      `/api/v0/workspaces/${encodeURIComponent(workspaceId)}/members`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      },
    ),
  createInvite: (
    workspaceId: string,
    email: string,
    role: Exclude<WorkspaceRole, 'owner'> = 'member',
    token?: string,
  ) =>
    request<{ email: string; role: Exclude<WorkspaceRole, 'owner'> }>(
      `/api/v0/workspaces/${encodeURIComponent(workspaceId)}/invites`,
      {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: json({ email, role }),
        credentials: 'include',
      },
    ),
  deleteInvite: (workspaceId: string, email: string, token?: string) =>
    request<{ ok: boolean; withdrawn: boolean }>(
      `/api/v0/workspaces/${encodeURIComponent(workspaceId)}/invites/${encodeURIComponent(email)}`,
      {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      },
    ),
  deleteMember: (workspaceId: string, actorId: string, token?: string) =>
    request<{ ok: boolean; removed: boolean }>(
      `/api/v0/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(actorId)}`,
      {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      },
    ),
  updateMemberRole: (workspaceId: string, actorId: string, role: Exclude<WorkspaceRole, 'owner'>) =>
    request<unknown>(
      `/api/v0/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(actorId)}`,
      { method: 'PATCH', body: json({ role }) },
    ),

  // Organizations (plan 19): the enterprise boundary above workspaces.
  myOrgs: () => request<{ orgs: OrgListItem[] }>('/api/v0/me/orgs'),
  createOrg: (input: { name: string }) =>
    request<OrgListItem>('/api/v0/orgs', { method: 'POST', body: json(input) }),
  org: (orgId: string) => request<OrgDetail>(`/api/v0/orgs/${encodeURIComponent(orgId)}`),
  updateOrg: (orgId: string, input: { name: string }) =>
    request<OrgListItem>(`/api/v0/orgs/${encodeURIComponent(orgId)}`, {
      method: 'PATCH',
      body: json(input),
    }),
  orgMembers: (orgId: string) =>
    request<{ members: OrgMember[]; invites: OrgInvite[] }>(
      `/api/v0/orgs/${encodeURIComponent(orgId)}/members`,
    ),
  createOrgInvite: (orgId: string, input: { email: string; role: Exclude<OrgRole, 'owner'> }) =>
    request<{ email: string; role: Exclude<OrgRole, 'owner'> }>(
      `/api/v0/orgs/${encodeURIComponent(orgId)}/invites`,
      { method: 'POST', body: json(input) },
    ),
  deleteOrgInvite: (orgId: string, email: string) =>
    request<unknown>(
      `/api/v0/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    ),
  updateOrgMemberRole: (orgId: string, actorId: string, role: OrgRole) =>
    request<unknown>(
      `/api/v0/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(actorId)}`,
      { method: 'PATCH', body: json({ role }) },
    ),
  deleteOrgMember: (orgId: string, actorId: string) =>
    request<unknown>(
      `/api/v0/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(actorId)}`,
      { method: 'DELETE' },
    ),

  // Sources: the systems Traceira reads for a workspace (Settings → Sources).
  listContextSources: (workspaceId: string) =>
    request<{ sources: ContextSource[] }>(contextPath(workspaceId, '/sources')),
  /**
   * Returns the provider page that installs or approves Traceira, or `connected` for an MCP
   * connector (its `name`, `description` and `url`, with a pasted `token` in `Authorization` or the
   * `tokenHeader` named) whose server took the key or needs no sign-in.
   */
  startContextConnect: (
    workspaceId: string,
    input: ContextConnectInput & {
      name?: string;
      description?: string;
      url?: string;
      token?: string;
      tokenHeader?: string;
    },
  ) =>
    request<{ url?: string; connected?: boolean }>(contextPath(workspaceId, '/connect'), {
      method: 'POST',
      body: json(input),
    }),
  /** A pasted token: a self-managed GitLab (`config.url`) or a Bitbucket workspace (`config.workspace`). */
  connectContextSource: (
    workspaceId: string,
    input: ContextConnectInput & {
      credential: { token: string };
      config: { url: string } | { workspace: string };
    },
  ) =>
    request<{ source: ContextSource }>(contextPath(workspaceId, '/sources'), {
      method: 'POST',
      body: json(input),
    }),
  updateContextSource: (
    workspaceId: string,
    sourceId: string,
    input: Partial<Omit<ContextConnectInput, 'kind'>> & {
      /**
       * A git host's picked repositories, or an MCP connector's name, description and access: the
       * tools Traceira may call (`[]` clears them), and whether it reads the server's resources and
       * keeps a searchable copy.
       */
      config?:
        | { repositories: string[] }
        | {
            name: string;
            description: string;
            allowedTools: string[];
            readResources?: boolean;
            indexResources?: boolean;
          };
      /** A git host's webhook secret, added, or removed by its fingerprint. */
      credential?: { addWebhookSecret: string } | { removeWebhookSecret: string };
    },
  ) =>
    request<{ source: ContextSource }>(
      contextPath(workspaceId, `/sources/${encodeURIComponent(sourceId)}`),
      { method: 'PATCH', body: json(input) },
    ),
  disconnectContextSource: (workspaceId: string, sourceId: string) =>
    request<{ disconnected: boolean }>(
      contextPath(workspaceId, `/sources/${encodeURIComponent(sourceId)}`),
      { method: 'DELETE' },
    ),
  contextOrgSettings: (orgId: string) =>
    request<ContextOrgSettings>(`/api/v1/orgs/${encodeURIComponent(orgId)}/context/settings`),
  updateContextOrgSettings: (orgId: string, input: ContextOrgSettings) =>
    request<ContextOrgSettings>(`/api/v1/orgs/${encodeURIComponent(orgId)}/context/settings`, {
      method: 'PATCH',
      body: json(input),
    }),

  authorizationConfig: () => request<AuthorizationConfig>('/api/v0/authorization/config'),
  authorizations: (id: string, session: string) =>
    request<{ letters: Letter[] }>(`/api/v0/workspaces/${id}/authorizations`, {
      headers: credentials(session, ''),
    }),
  createAuthorization: (id: string, session: string, input: AuthorizationInput) =>
    request<Letter>(`/api/v0/workspaces/${id}/authorizations`, {
      method: 'POST',
      headers: credentials(session, ''),
      body: json(input),
    }),
  authorizationDomainConnect: (id: string, session: string) =>
    request<ServerDomainConnect>(`/api/v0/authorization/${id}/domain-connect`, {
      headers: credentials(session, ''),
    }),
  resendAuthorization: (id: string, session: string) =>
    request(`/api/v0/authorization/${id}/resend`, {
      method: 'POST',
      headers: { 'X-Traceira-Session': session },
    }),
  previewAuthorizationCode: (id: string, session: string, code: string) =>
    request<Letter & { challengeId: string; linkExpiresAt: string }>(
      `/api/v0/authorization/${id}/code-preview`,
      {
        method: 'POST',
        headers: { 'X-Traceira-Session': session },
        body: json({ code }),
      },
    ),
  confirmAuthorizationCode: (
    id: string,
    session: string,
    code: string,
    challengeId: string,
    termsHash: string,
  ) =>
    request<Letter & { ownerToken: string }>(`/api/v0/authorization/${id}/code-confirm`, {
      method: 'POST',
      headers: { 'X-Traceira-Session': session },
      body: json({ code, challengeId, termsHash, accepted: true }),
    }),
  previewAuthorization: (token: string) =>
    request<Letter & { purpose: 'approve' | 'manage'; linkExpiresAt: string }>(
      '/api/v0/authorization/preview',
      { method: 'POST', body: json({ token }) },
    ),
  confirmAuthorization: (token: string, termsHash: string) =>
    request<Letter & { ownerToken: string }>('/api/v0/authorization/confirm', {
      method: 'POST',
      body: json({ token, termsHash, accepted: true }),
    }),
  authorizationWorkspace: (id: string, session = '', token = '') =>
    request<WorkspaceSession>(`/api/v0/authorization/${id}/workspace`, {
      headers: {
        ...(session ? { 'X-Traceira-Session': session } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }),
  /** The signed letter as published in the customer's own DNS (_traceira-authz TXT). */
  domainLetter: (domain: string) =>
    request<DomainLetterResult>(
      `/api/v0/public/domain-letter?domain=${encodeURIComponent(domain)}`,
    ),
  /** Certificate PDF rendered from that DNS letter (application/pdf attachment). */
  domainLetterPdf: (domain: string) =>
    `${API_URL}/api/v0/public/domain-letter/pdf?domain=${encodeURIComponent(domain)}`,
  domainLetterMd: (domain: string) =>
    `${API_URL}/api/v0/public/domain-letter/md?domain=${encodeURIComponent(domain)}`,
  publicKeys: () => request<{ keys: (JsonWebKey & { kid: string })[] }>('/api/v0/public/jwks'),
  managementEmail: (id: string) =>
    request(`/api/v0/authorization/${id}/management-email`, { method: 'POST' }),
  ownerAuthorization: (id: string, token: string) =>
    request<Letter & { privateJws: string }>(`/api/v0/authorization/${id}/owner`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  revokeAuthorization: (id: string, token: string) =>
    request<Letter>(`/api/v0/authorization/${id}/revoke`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }),
  createSession: (input: {
    organization: string;
    domain: string;
    authorizer: { name: string; email: string };
    useParentDomain: boolean;
    /** An organization the signed-in person may add workspaces to; omitted, a new one. */
    organizationId?: string;
  }) => request<ServerSession>('/api/v0/onboarding', { method: 'POST', body: json(input) }),
  updateSession: (
    id: string,
    input: {
      organization: string;
      domain: string;
      authorizer: { name: string; email: string };
      useParentDomain: boolean;
      organizationId?: string;
    },
  ) => request<ServerSession>(`/api/v0/onboarding/${id}`, { method: 'PUT', body: json(input) }),
  setMethod: (id: string, method: 'CNAME' | 'TXT') =>
    request<ServerSession>(`/api/v0/onboarding/${id}/method`, {
      method: 'PUT',
      body: json({ method }),
    }),
  checkRecord: (id: string) =>
    request<{ session: ServerSession; lines: ServerLine[] }>(
      `/api/v0/onboarding/${id}/record-check`,
      {
        method: 'POST',
      },
    ),
  setConsent: (id: string, accepted: boolean) =>
    request<ServerSession>(`/api/v0/onboarding/${id}/consent`, {
      method: 'PUT',
      body: json({ accepted }),
    }),
  domainConnect: (id: string) =>
    request<ServerDomainConnect>(`/api/v0/onboarding/${id}/domain-connect`),
  createWorkspace: (id: string) =>
    request<ServerWorkspace>(`/api/v0/onboarding/${id}/workspace`, { method: 'POST' }),
  createContainer: (id: string) =>
    request<ContainerView>(`/api/v0/onboarding/${id}/container`, { method: 'POST' }),
  getContainer: (id: string) => request<ContainerView>(`/api/v0/onboarding/${id}/container`),
  // The backend requires a credential for this read: setup session, owner token or sign-in token.
  getWorkspaceContainer: (id: string, session = '', token = '') =>
    request<ContainerView>(`/api/v0/workspaces/${id}/container`, {
      headers: credentials(session, token),
    }),
  // Either credential is enough; a header is sent only when its value is non-empty.
  activateWorkspaceContainer: (id: string, session = '', token = '') =>
    request<ContainerView>(`/api/v0/workspaces/${id}/container`, {
      method: 'POST',
      headers: credentials(session, token),
    }),
  // Terminal access follows workspace membership, so these carry only the session cookie.
  openTerminal: (authorizationId: string, size: { cols: number; rows: number }) =>
    request<TerminalTicket>(terminalSessions(authorizationId), {
      method: 'POST',
      body: json(size),
    }),
  /** No body, so no Content-Type (an empty JSON body is a 400). 204 means terminated. */
  stopTerminal: (authorizationId: string, sessionId: string) =>
    request<undefined>(`${terminalSessions(authorizationId)}/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    }),
  terminalSocket: (webSocketPath: string) => {
    // Same-origin API path only, whatever the server sends.
    if (!webSocketPath.startsWith('/api/')) throw new Error('The terminal address is invalid.');
    return new WebSocket(
      `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${webSocketPath}`,
      'traceira.workspace-terminal.v1',
    );
  },

  // Engagement cockpit (v0; factory job 004 §4.9). The real backend does not
  // implement these yet; the UI renders its documented unavailable state.
  engagement: (id: string) =>
    request<EngagementView>(`/api/v0/engagements/${encodeURIComponent(id)}`),
  engagementRun: (id: string) =>
    request<RunView>(`/api/v0/engagements/${encodeURIComponent(id)}/run`),
  engagementRunEvents: (id: string, opts: { after?: number; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.after !== undefined) params.set('after', String(opts.after));
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    const query = params.toString();
    return request<RunEventsPage>(
      `/api/v0/engagements/${encodeURIComponent(id)}/run/events${query ? `?${query}` : ''}`,
    );
  },
  startEngagementRun: (id: string) =>
    request<RunView>(`/api/v0/engagements/${encodeURIComponent(id)}/run`, { method: 'POST' }),
  stopEngagementRun: (id: string) =>
    request<RunView>(`/api/v0/engagements/${encodeURIComponent(id)}/run/stop`, { method: 'POST' }),
  // Engagement memory: not served by the backend yet; a 404 renders the unavailable state.
  engagementMemory: (id: string) =>
    request<{ entries: MemoryEntry[] }>(`/api/v0/engagements/${encodeURIComponent(id)}/memory`),
  addEngagementMemory: (id: string, text: string) =>
    request<MemoryEntry>(`/api/v0/engagements/${encodeURIComponent(id)}/memory`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  // Workspace discovery capability (v1 provisional; W12/W13/W16)
  getDiscoveryEligibility: (workspaceId: string, session?: string, token?: string) =>
    request<TraceiraWorkspaceDiscoveryCapabilityEligibilityV1Provisional>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/eligibility`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  getLatestDiscoveryAttempt: async (
    workspaceId: string,
    session?: string,
    token?: string,
    withoutEvidence = false,
  ) => {
    const latest = await request<
      | TraceiraWorkspaceDiscoveryRecordsV1
      | { contractVersion: 'workspace-discovery/v1'; attempt: null }
      | null
    >(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/attempts/latest${withoutEvidence ? '?evidence=none' : ''}`,
      { headers: credentials(session ?? '', token ?? '') },
    );
    return latest?.attempt ? (latest as TraceiraWorkspaceDiscoveryRecordsV1) : null;
  },
  /** Evidence of one attempt that arrived after `since`, in ingest order. */
  getDiscoveryEvidenceSince: (
    workspaceId: string,
    attemptId: string,
    since: number,
    session?: string,
    token?: string,
  ) =>
    request<{
      evidence: TraceiraWorkspaceDiscoveryRecordsV1['evidence'];
      cursor: number;
      more: boolean;
    }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/attempts/${encodeURIComponent(attemptId)}/evidence?since=${since}`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  getDiscoveryEvidence: (workspaceId: string, session?: string, token?: string, after?: string) =>
    request<{
      evidence: WorkspaceEvidence[];
      sourceRecords: WorkspaceSourceRecord[];
      nextCursor: string | null;
    }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/evidence${after ? `?after=${encodeURIComponent(after)}` : ''}`,
      {
        headers: credentials(session ?? '', token ?? ''),
      },
    ),
  getDiscoveryAttempt: (workspaceId: string, attemptId: string, session?: string, token?: string) =>
    request<TraceiraWorkspaceDiscoveryRecordsV1>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/attempts/${encodeURIComponent(attemptId)}`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  getDiscoveryRecords: (
    workspaceId: string,
    attemptId?: string,
    session?: string,
    token?: string,
  ) => {
    const url = attemptId
      ? `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/records?attemptId=${encodeURIComponent(attemptId)}`
      : `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/records`;
    return request<TraceiraWorkspaceDiscoveryRecordsV1>(url, {
      headers: credentials(session ?? '', token ?? ''),
    });
  },
  getDiscoveryProgress: (workspaceId: string, session?: string, token?: string) =>
    request<DiscoveryProgressResponse>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/progress`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  getDiscoveryFindings: (
    workspaceId: string,
    session?: string,
    token?: string,
    filters: { severity?: string; lane?: 'DNS' | 'Network' | 'Mixed' } = {},
  ) => {
    const params = new URLSearchParams();
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.lane) params.set('lane', filters.lane);
    const query = params.toString();
    return request<WorkspaceFinding[]>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings${query ? `?${query}` : ''}`,
      { headers: credentials(session ?? '', token ?? '') },
    );
  },
  getDiscoveryEvidenceRecord: (
    workspaceId: string,
    evidenceId: string,
    session?: string,
    token?: string,
  ) =>
    request<WorkspaceEvidence>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/evidence/${encodeURIComponent(evidenceId)}`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  getDiscoveryFinding: (workspaceId: string, findingId: string, session?: string, token?: string) =>
    request<WorkspaceFinding>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  startDiscovery: (
    workspaceId: string,
    input: StartDiscoveryRequest,
    idempotencyKey: string,
    session?: string,
    token?: string,
  ) => {
    if (!/^[\x21-\x7E]{1,200}$/.test(idempotencyKey)) {
      throw new TypeError('A valid idempotency key is required to start discovery.');
    }
    return request<StartDiscoveryResponse>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/start`,
      {
        method: 'POST',
        headers: {
          ...credentials(session ?? '', token ?? ''),
          'Idempotency-Key': idempotencyKey,
        },
        body: json(input),
      },
    );
  },
  stopDiscovery: (workspaceId: string, session?: string, token?: string) =>
    request<DiscoveryStopResponse>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/stop`,
      {
        method: 'POST',
        headers: credentials(session ?? '', token ?? ''),
      },
    ),
  // The signed-in person's remembered terminal questions in this workspace, oldest first.
  getWorkspaceQuestions: (workspaceId: string, session?: string, token?: string) =>
    request<{
      turns: {
        turnId: string;
        question: string;
        answer: string;
        verified: boolean | null;
        askedAt: string;
        answeredAt?: string;
      }[];
    }>(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/terminal/questions`, {
      headers: credentials(session ?? '', token ?? ''),
    }),
  // The coordinator answers from this workspace's records and checks the answer against them.
  askWorkspaceQuestion: (workspaceId: string, question: string, session?: string, token?: string) =>
    request<{
      answer: string;
      rationale?: string;
      verified: boolean | null;
      answeredAt?: string;
    }>(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/terminal/questions`, {
      method: 'POST',
      headers: credentials(session ?? '', token ?? ''),
      body: JSON.stringify({ question }),
    }),
  // Streams the coordinator's live reasoning, then the answer, over server-sent events.
  askWorkspaceQuestionStream: async (
    workspaceId: string,
    question: string,
    handlers: AnswerStreamHandlers,
    session?: string,
    token?: string,
  ): Promise<void> => {
    const res = await send(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/terminal/questions/stream`,
      {
        method: 'POST',
        headers: credentials(session ?? '', token ?? ''),
        body: JSON.stringify({ question }),
      },
    );
    await consumeAnswerStream(res, handlers);
  },
  // A deliberate finding action creates or returns that finding's one engagement (FLOW-05).
  investigateFinding: (
    workspaceId: string,
    findingId: string,
    idempotencyKey: string,
    session?: string,
    token?: string,
  ) =>
    request<{ engagementId: string; replay: boolean }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/investigate`,
      {
        method: 'POST',
        headers: { ...credentials(session ?? '', token ?? ''), 'Idempotency-Key': idempotencyKey },
      },
    ),

  // Review decisions on a finding, newest first; recording one appends to that history.
  getFindingReviews: (workspaceId: string, findingId: string, session?: string, token?: string) =>
    request<{ reviews: FindingReview[] }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/reviews`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  reviewFinding: (
    workspaceId: string,
    findingId: string,
    input: { decision: ReviewDecision; rationale: string; citedEvidenceIds: string[] },
    session?: string,
    token?: string,
  ) =>
    request<FindingReview>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/reviews`,
      {
        method: 'POST',
        headers: credentials(session ?? '', token ?? ''),
        body: JSON.stringify(input),
      },
    ),

  auditLog: (
    workspaceId: string,
    /** `{}` for the newest page, `{ before }` for older events, `{ after }` for newer ones. */
    cursor: { before: string } | { after: string } | Record<string, never>,
    session?: string,
    token?: string,
  ) =>
    request<AuditLogPage>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/audit-log?${new URLSearchParams(cursor)}`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  /** Audit events per day over the last year, dated in `timeZone`. */
  auditActivity: (workspaceId: string, timeZone: string, session?: string, token?: string) =>
    request<AuditActivity>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/audit-log/activity?${new URLSearchParams({ tz: timeZone })}`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  // Remediation of a finding: one record, replaced on save, and appended re-tests.
  listRemediations: (workspaceId: string, session?: string, token?: string) =>
    request<{ remediations: RemediationRecord[] }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/remediations`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  getFindingRemediation: (
    workspaceId: string,
    findingId: string,
    session?: string,
    token?: string,
  ) =>
    request<{ remediation: RemediationRecord | null; retests: RetestRecord[] }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/remediation`,
      { headers: credentials(session ?? '', token ?? '') },
    ),
  saveFindingRemediation: (
    workspaceId: string,
    findingId: string,
    input: RemediationInput,
    session?: string,
    token?: string,
  ) =>
    request<RemediationRecord>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/remediation`,
      {
        method: 'PUT',
        headers: credentials(session ?? '', token ?? ''),
        body: JSON.stringify(input),
      },
    ),
  /** Asks the coordinator for the finding's plan; answers at once with the plan PLANNING. */
  requestRemediationPlan: (
    workspaceId: string,
    findingId: string,
    session?: string,
    token?: string,
    regenerate = false,
  ) =>
    request<RemediationRecord>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/remediation/plan${regenerate ? '?regenerate=true' : ''}`,
      { method: 'POST', headers: credentials(session ?? '', token ?? '') },
    ),
  recordFindingRetest: (
    workspaceId: string,
    findingId: string,
    input: RetestInput,
    session?: string,
    token?: string,
  ) =>
    request<RetestRecord>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/discovery/findings/${encodeURIComponent(findingId)}/retests`,
      {
        method: 'POST',
        headers: credentials(session ?? '', token ?? ''),
        body: JSON.stringify(input),
      },
    ),

  // Report revisions are saved in the workspace; an export renders the saved revision.
  listReportRevisions: (workspaceId: string) =>
    request<{ reports: ReportRevision[] }>(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/reports`,
    ),
  createReportRevision: (
    workspaceId: string,
    input: { audience: ReportRevision['audience']; findingIds: string[]; title?: string },
  ) =>
    request<ReportRevision>(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}/reports`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  exportReportRevision: async (
    workspaceId: string,
    reportId: string,
    revisionNumber: number,
    format: ReportFormat,
  ) => {
    const res = await send(
      `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(reportId)}/revisions/${revisionNumber}/export?format=${format}`,
    );
    const filename = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
    return { blob: await res.blob(), filename: filename ?? `report-r${revisionNumber}.${format}` };
  },

  // Evidence-only v1 records. Finding, report, import-job, evidence, source
  // record, and workflow-run payloads share the generated contracts/v1 types.
  findings: (scope: RecordsScope, filters: FindingFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.origin) params.set('origin', filters.origin);
    if (filters.analystReviewState) params.set('analystReviewState', filters.analystReviewState);
    if (filters.severity) params.set('severity', filters.severity);
    const query = params.toString();
    return request<Finding[]>(`${evidenceV1Path('findings')}${query ? `?${query}` : ''}`, {
      headers: evidenceV1ScopeHeaders(scope),
    });
  },
  finding: (scope: RecordsScope, findingId: string) =>
    request<Finding>(evidenceV1Path('findings', findingId), {
      headers: evidenceV1ScopeHeaders(scope),
    }),
  evidence: (scope: RecordsScope, evidenceId: string) =>
    request<EvidenceRecord>(evidenceV1Path('evidence', evidenceId), {
      headers: evidenceV1ScopeHeaders(scope),
    }),
  sourceRecord: (scope: RecordsScope, sourceRecordId: string) =>
    request<SourceRecord>(evidenceV1Path('source-records', sourceRecordId), {
      headers: evidenceV1ScopeHeaders(scope),
    }),
  report: (scope: RecordsScope, reportId: string) =>
    request<EvidenceReport>(evidenceV1Path('reports', reportId), {
      headers: evidenceV1ScopeHeaders(scope),
    }),
  createReport: (scope: RecordsScope, input: CreateReportInput) =>
    request<EvidenceReport>(evidenceV1Path('reports'), {
      method: 'POST',
      headers: evidenceV1ScopeHeaders(scope),
      body: json(input),
    }),
  createImportJob: (scope: RecordsScope, input: CreateImportJobInput) =>
    request<ImportJob>(evidenceV1Path('import-jobs'), {
      method: 'POST',
      headers: evidenceV1ScopeHeaders(scope),
      body: json(input),
    }),
  importJob: (scope: RecordsScope, importJobId: string) =>
    request<ImportJob>(evidenceV1Path('import-jobs', importJobId), {
      headers: evidenceV1ScopeHeaders(scope),
    }),
  workflowRun: (scope: RecordsScope, workflowRunId: string) =>
    request<WorkflowRun>(evidenceV1Path('workflow-runs', workflowRunId), {
      headers: evidenceV1ScopeHeaders(scope),
    }),

  /** Streams the domain check (NDJSON). Aborting the signal stops it server-side. */
  async domainCheck(
    id: string,
    signal: AbortSignal,
    onLine: (line: ServerLine) => void,
  ): Promise<ServerCheck | null> {
    let res: Response;
    try {
      res = await fetch(`${API_URL}/api/v0/onboarding/${id}/domain-check`, {
        method: 'POST',
        signal,
      });
    } catch (error) {
      if (signal.aborted) return null;
      throw new ApiError(
        0,
        'offline',
        "We can't reach Traceira right now. Your input is saved; try again in a moment.",
        {
          error: String(error),
        },
      );
    }
    if (!res.ok || !res.body) {
      const body = (await readJson(res)) as Record<string, unknown>;
      throw new ApiError(
        res.status,
        String(body.error ?? 'error'),
        String(body.message ?? res.statusText),
        body,
      );
    }
    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    let result: ServerCheck | null = null;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += value;
        let nl: number;
        while ((nl = buffer.indexOf('\n')) >= 0) {
          const event = JSON.parse(buffer.slice(0, nl), withoutToolNames) as
            { type: 'line'; line: ServerLine } | { type: 'result'; result: ServerCheck };
          buffer = buffer.slice(nl + 1);
          if (event.type === 'line') onLine(event.line);
          else result = event.result;
        }
      }
    } catch (error) {
      if (signal.aborted) return null;
      throw error;
    }
    return result;
  },
};
